import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileText,
  GraduationCap,
  Mail,
  MessageSquareWarning,
  Pill,
  PoundSterling,
  RefreshCw,
  Send,
  ShieldAlert,
  ToggleLeft,
  ToggleRight,
  UserRoundCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/core/hooks';

type AutomationCategory =
  | 'Visits'
  | 'Care Plans'
  | 'Documents'
  | 'Training'
  | 'Billing'
  | 'Medication'
  | 'Incidents'
  | 'Complaints'
  | 'Payroll';

type AutomationStatus = 'enabled' | 'disabled';
type ReminderPriority = 'critical' | 'high' | 'medium' | 'low';
type DeliveryChannel = 'In-app' | 'Email' | 'SMS' | 'Push';
type ServerChannel = 'email' | 'sms' | 'push';

interface AutomationRule {
  id: string;
  title: string;
  category: AutomationCategory;
  description: string;
  trigger: string;
  recipient: string;
  action: string;
  priority: ReminderPriority;
  defaultChannels: DeliveryChannel[];
  channels?: ServerChannel[];
  enabled?: boolean;
  intervalMinutes?: number;
  lastRunAt?: string;
  nextRunAt: string;
  maxAttempts?: number;
}

interface ReminderLog {
  id: string;
  ruleId: string;
  title: string;
  recipient: string;
  message: string;
  sentAt: string;
  priority: ReminderPriority;
  status: 'Sent' | 'Queued' | 'Blocked' | 'SENT' | 'RETRY_SCHEDULED' | 'FAILED';
  channel?: ServerChannel;
  attempts?: number;
  provider?: string;
  error?: string;
  nextRetryAt?: string;
}

const automationRules: AutomationRule[] = [
  {
    id: 'carer-before-visit',
    title: 'Automatic carer reminder before visit',
    category: 'Visits',
    description: 'Sends the assigned carer a reminder before the scheduled time with the client, address and key tasks.',
    trigger: '30 minutes before scheduled visit',
    recipient: 'Assigned carer',
    action: 'Open visit details, directions and check-in checklist.',
    priority: 'medium',
    defaultChannels: ['Push', 'In-app'],
    nextRunAt: minutesFromNow(18),
  },
  {
    id: 'admin-visit-not-started',
    title: 'Admin reminder when a visit has not started',
    category: 'Visits',
    description: 'Alerts the administrator when there is no valid check-in after the scheduled start time.',
    trigger: '10 minutes after scheduled start without check-in',
    recipient: 'Administrator / Coordinator',
    action: 'Call carer, check live location, reassign if required.',
    priority: 'critical',
    defaultChannels: ['In-app', 'SMS', 'Email'],
    nextRunAt: minutesFromNow(7),
  },
  {
    id: 'care-plan-review',
    title: 'Care plan review reminder',
    category: 'Care Plans',
    description: 'Creates reminders for care plans that are close to expiry or have not been reviewed.',
    trigger: '14 days before care plan review date',
    recipient: 'Registered manager / Care coordinator',
    action: 'Open care plan, review changes, update risks and tasks.',
    priority: 'medium',
    defaultChannels: ['In-app', 'Email'],
    nextRunAt: daysFromNow(2),
  },
  {
    id: 'expired-document',
    title: 'Expired document reminder',
    category: 'Documents',
    description: 'Flags expiring carer, client and company documents before they become overdue.',
    trigger: '30 / 14 / 7 days before document expiry',
    recipient: 'Administrator',
    action: 'Request updated document and record evidence.',
    priority: 'high',
    defaultChannels: ['In-app', 'Email'],
    nextRunAt: daysFromNow(1),
  },
  {
    id: 'expired-training',
    title: 'Expired training reminder',
    category: 'Training',
    description: 'Warns when carer training is approaching expiry or is already expired.',
    trigger: '30 / 14 / 7 days before training expiry',
    recipient: 'Administrator and carer',
    action: 'Book training and block high-risk allocation if mandatory training is expired.',
    priority: 'high',
    defaultChannels: ['In-app', 'Email', 'Push'],
    nextRunAt: daysFromNow(3),
  },
  {
    id: 'invoice-unpaid',
    title: 'Unpaid invoice reminder',
    category: 'Billing',
    description: 'Sends follow-up reminders for unpaid invoices and flags them in the ageing report.',
    trigger: 'On due date and every 7 days after due date',
    recipient: 'Billing admin / payer contact',
    action: 'Send payment reminder and update invoice status.',
    priority: 'medium',
    defaultChannels: ['Email', 'In-app'],
    nextRunAt: hoursFromNow(5),
  },
  {
    id: 'medication-missed',
    title: 'Missed medication reminder',
    category: 'Medication',
    description: 'Alerts the team when a scheduled medication is not confirmed in eMAR.',
    trigger: 'Medication due time passes without GIVEN / REFUSED / HELD record',
    recipient: 'Registered manager / Admin',
    action: 'Open eMAR escalation, call carer and document outcome.',
    priority: 'critical',
    defaultChannels: ['In-app', 'SMS', 'Email'],
    nextRunAt: minutesFromNow(4),
  },
  {
    id: 'incident-no-follow-up',
    title: 'Incident follow-up reminder',
    category: 'Incidents',
    description: 'Alerts the registered manager when an incident does not have timely follow-up or investigation notes.',
    trigger: '24 hours after incident report without follow-up note',
    recipient: 'Registered manager',
    action: 'Open incident, complete follow-up and record outcome.',
    priority: 'high',
    defaultChannels: ['In-app', 'Email'],
    nextRunAt: hoursFromNow(9),
  },
  {
    id: 'complaint-no-response',
    title: 'Complaint response reminder',
    category: 'Complaints',
    description: 'Tracks complaints without a response and monitors acknowledgement deadlines.',
    trigger: '48 hours after complaint received without response',
    recipient: 'Registered manager / Complaints owner',
    action: 'Acknowledge complaint, assign owner and record response deadline.',
    priority: 'high',
    defaultChannels: ['In-app', 'Email'],
    nextRunAt: hoursFromNow(12),
  },
  {
    id: 'payroll-ready',
    title: 'Payroll ready reminder',
    category: 'Payroll',
    description: 'Notifies the payroll administrator when verified hours are ready for weekly or monthly payroll.',
    trigger: 'End of week/month after verified timesheets are complete',
    recipient: 'Payroll admin',
    action: 'Open carer payroll, review variances and generate PDF payroll.',
    priority: 'medium',
    defaultChannels: ['In-app', 'Email'],
    nextRunAt: daysFromNow(4),
  },
];

const categoryIcons: Record<AutomationCategory, ReactNode> = {
  Visits: <CalendarClock className="h-5 w-5" />,
  'Care Plans': <ClipboardCheck className="h-5 w-5" />,
  Documents: <FileText className="h-5 w-5" />,
  Training: <GraduationCap className="h-5 w-5" />,
  Billing: <Mail className="h-5 w-5" />,
  Medication: <Pill className="h-5 w-5" />,
  Incidents: <ShieldAlert className="h-5 w-5" />,
  Complaints: <MessageSquareWarning className="h-5 w-5" />,
  Payroll: <PoundSterling className="h-5 w-5" />,
};

const priorityStyles: Record<ReminderPriority, string> = {
  critical: 'border-red-200 bg-red-50 text-red-950',
  high: 'border-amber-200 bg-amber-50 text-amber-950',
  medium: 'border-blue-200 bg-blue-50 text-blue-950',
  low: 'border-slate-200 bg-slate-50 text-slate-950',
};

export function PracticalAutomations() {
  const { token } = useAuth();
  const [serverRules, setServerRules] = useState<AutomationRule[]>(automationRules);
  const [logs, setLogs] = useState<ReminderLog[]>(defaultLogs);
  const [pushCount, setPushCount] = useState(0);
  const [schedulerOnline, setSchedulerOnline] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [category, setCategory] = useState<'All' | AutomationCategory>('All');

  const authHeaders = useMemo(() => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);
  const enabledRules = serverRules.filter((rule) => rule.enabled !== false);
  const criticalDue = enabledRules.filter((rule) => rule.priority === 'critical' && new Date(rule.nextRunAt) <= new Date(Date.now() + 15 * 60 * 1000));
  const filteredRules = useMemo(
    () => serverRules.filter((rule) => category === 'All' || rule.category === category),
    [category, serverRules]
  );

  const loadServerAutomations = async () => {
    try {
      const response = await fetch('/api/automations/reminders', { credentials: 'include' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load server automations');
      setServerRules((payload.rules || automationRules).map(normalizeServerRule));
      setLogs((payload.logs || []).map(normalizeServerLog).concat(defaultLogs).slice(0, 50));
      setPushCount(payload.pushNotifications?.length || 0);
      setSchedulerOnline(Boolean(payload.scheduler?.runsOnServer));
    } catch {
      setSchedulerOnline(false);
    }
  };

  useEffect(() => {
    void loadServerAutomations();
    const intervalId = window.setInterval(() => void loadServerAutomations(), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  const toggleRule = async (ruleId: string) => {
    const currentRule = serverRules.find((rule) => rule.id === ruleId);
    if (!currentRule) return;
    const nextEnabled = currentRule.enabled === false;
    setServerRules((items) => items.map((rule) => rule.id === ruleId ? { ...rule, enabled: nextEnabled } : rule));
    try {
      const response = await fetch(`/api/automations/reminders/rules/${ruleId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to update automation');
      toast.success(`${currentRule.title} ${nextEnabled ? 'enabled' : 'disabled'} on server`);
      await loadServerAutomations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update automation');
      await loadServerAutomations();
    }
  };

  const runAutomationCheck = async () => {
    setIsRunning(true);
    try {
      const response = await fetch('/api/automations/reminders/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to run scheduler');
      setServerRules((payload.rules || serverRules).map(normalizeServerRule));
      setLogs((payload.logs || []).map(normalizeServerLog).slice(0, 50));
      setPushCount(payload.pushNotifications?.length || 0);
      toast.success(`${payload.processed ?? 0} server reminder delivery attempt(s) processed`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to run scheduler');
    } finally {
      setIsRunning(false);
    }
  };

  const sendBrowserNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser notifications are not supported');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      toast.error('Notification permission was not granted');
      return;
    }

    enabledRules.slice(0, 5).forEach((rule) => {
      new Notification(rule.title, {
        body: `${rule.recipient}: ${rule.action}`,
        tag: rule.id,
      });
    });
      toast.success('Browser test notification shown. Real reminders now run on the server.');
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <BellRing className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Practical Automations</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Automation & Reminder Centre</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Central control for operational reminders: visits, care plans, documents, training, invoices,
                medication, incidents, complaints and payroll readiness.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runAutomationCheck}
              disabled={isRunning}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
            >
              <RefreshCw className="h-4 w-4" />
              {isRunning ? 'Running...' : 'Run Server Check'}
            </button>
            <button
              type="button"
              onClick={sendBrowserNotifications}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Send className="h-4 w-4" />
              Test Notifications
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Active automations" value={String(enabledRules.length)} icon={<CheckCircle2 className="h-5 w-5" />} />
        <MetricCard label="Critical due soon" value={String(criticalDue.length)} icon={<AlertTriangle className="h-5 w-5" />} tone={criticalDue.length ? 'danger' : 'default'} />
        <MetricCard label="Retry / queued" value={String(logs.filter((log) => ['Queued', 'RETRY_SCHEDULED'].includes(log.status)).length)} icon={<Clock className="h-5 w-5" />} />
        <MetricCard label="Push outbox" value={String(pushCount)} icon={<UserRoundCheck className="h-5 w-5" />} />
      </section>

      <section className={`rounded-lg border p-4 ${schedulerOnline ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <div className={`flex gap-3 text-sm leading-6 ${schedulerOnline ? 'text-emerald-950' : 'text-amber-950'}`}>
          <BellRing className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            Server scheduler is {schedulerOnline ? 'active' : 'not reachable from this browser'}.
            Reminder checks now run in the backend even when the browser is closed, with delivery logs and retry scheduling for failed email/SMS/push attempts.
          </p>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-bold text-slate-950">Automation Rules</h2>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value as 'All' | AutomationCategory)}
            className="h-10 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="All">All categories</option>
            {Object.keys(categoryIcons).map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          {filteredRules.map((rule) => {
            const enabled = rule.enabled !== false;
            return (
              <article key={rule.id} className={`rounded-lg border p-5 shadow-sm ${enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-75'}`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex gap-3">
                    <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                      {categoryIcons[rule.category]}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-slate-950">{rule.title}</h3>
                        <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${priorityStyles[rule.priority]}`}>
                          {rule.priority}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{rule.description}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleRule(rule.id)}
                    className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold ${enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}
                  >
                    {enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    {enabled ? 'On' : 'Off'}
                  </button>
                </div>

                <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                  <Detail label="Trigger" value={rule.trigger} />
                  <Detail label="Recipient" value={rule.recipient} />
                  <Detail label="Next run" value={formatDateTime(rule.nextRunAt)} />
                  <Detail label="Channels" value={rule.defaultChannels.join(', ')} />
                </div>
                <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  <span className="font-semibold text-slate-950">Action:</span> {rule.action}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Reminder Queue & Log</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Reminder</th>
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(log.sentAt || log.nextRetryAt || new Date().toISOString())}</td>
                  <td className="px-4 py-3 font-medium text-slate-950">{log.title}</td>
                  <td className="px-4 py-3 text-slate-600">{log.recipient}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${priorityStyles[log.priority]}`}>
                      {log.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {log.status.replace('_', ' ')}
                    {log.channel ? <span className="ml-1 text-xs text-slate-400">({log.channel})</span> : null}
                    {log.attempts ? <span className="ml-1 text-xs text-slate-400">attempt {log.attempts}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{log.error || log.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: ReactNode;
  tone?: 'default' | 'danger';
}) {
  return (
    <article className={`rounded-lg border p-4 shadow-sm ${tone === 'danger' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeServerRule(rule: AutomationRule): AutomationRule {
  const channels = rule.channels || rule.defaultChannels?.map(toServerChannel).filter(Boolean) as ServerChannel[] || ['push'];
  return {
    ...rule,
    category: rule.category || 'Visits',
    priority: rule.priority || 'medium',
    enabled: rule.enabled !== false,
    defaultChannels: channels.map(toDisplayChannel),
    channels,
    nextRunAt: rule.nextRunAt || new Date().toISOString(),
  };
}

function normalizeServerLog(log: ReminderLog & { ruleTitle?: string; recipientName?: string; recipientEmail?: string; lastAttemptAt?: string; sentAt?: string }): ReminderLog {
  return {
    ...log,
    title: log.title || log.ruleTitle || log.ruleId,
    recipient: log.recipient || log.recipientName || log.recipientEmail || 'Recipient',
    sentAt: log.sentAt || log.lastAttemptAt || new Date().toISOString(),
    priority: log.priority || 'medium',
    message: log.message || log.error || '',
  };
}

function toDisplayChannel(channel: ServerChannel): DeliveryChannel {
  if (channel === 'email') return 'Email';
  if (channel === 'sms') return 'SMS';
  return 'Push';
}

function toServerChannel(channel: DeliveryChannel): ServerChannel {
  if (channel === 'Email') return 'email';
  if (channel === 'SMS') return 'sms';
  return 'push';
}

function minutesFromNow(minutes: number) {
  return new Date(Date.now() + minutes * 60 * 1000).toISOString();
}

function hoursFromNow(hours: number) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function daysFromNow(days: number) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

const defaultLogs: ReminderLog[] = [
  {
    id: 'default-reminder-1',
    ruleId: 'admin-visit-not-started',
    title: 'Admin reminder when a visit has not started',
    recipient: 'Administrator / Coordinator',
    message: 'Check-in has not started for the scheduled morning care visit. Open live carers and call assigned carer.',
    sentAt: minutesFromNow(-22),
    priority: 'critical',
    status: 'Sent',
  },
  {
    id: 'default-reminder-2',
    ruleId: 'invoice-unpaid',
    title: 'Unpaid invoice reminder',
    recipient: 'Billing admin / payer contact',
    message: 'Invoice INV-1004 is overdue. Send payment reminder and update status.',
    sentAt: hoursFromNow(-4),
    priority: 'medium',
    status: 'Queued',
  },
  {
    id: 'default-reminder-3',
    ruleId: 'payroll-ready',
    title: 'Payroll ready reminder',
    recipient: 'Payroll admin',
    message: 'Weekly verified carer hours are ready for payroll review.',
    sentAt: hoursFromNow(-8),
    priority: 'medium',
    status: 'Sent',
  },
];
